import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { JaMonthGridAbbrev } from './analysisDateLabelRich';

interface Props {
    /** 現在の分析期間の開始日（その月・年を選択状態・初期表示年に使う） */
    periodStart: Date;
    /** 例: ja-JP / en-US */
    locale: string;
    onSelectMonth: (firstDayOfMonth: Date) => void;
}

/**
 * 年の前後移動＋12ヶ月をグリッド表示する月選択 UI（カレンダーアプリの「月だけ選ぶ」レイアウト）
 */
export function AnalysisMonthGridPicker({ periodStart, locale, onSelectMonth }: Props): ReactElement {
    const { t } = useTranslation('analysis');
    const [viewYear, setViewYear] = useState(() => periodStart.getFullYear());

    useEffect(() => {
        setViewYear(periodStart.getFullYear());
    }, [periodStart]);

    const selectedYear = periodStart.getFullYear();
    const selectedMonth = periodStart.getMonth();

    const monthFormatter = useMemo(
        () => new Intl.DateTimeFormat(locale, { month: 'short' }),
        [locale],
    );

    const isJaLocale = locale.startsWith('ja');

    return (
        <div className="kotomil-month-grid-picker min-w-[16rem] px-1 pb-1 font-sans text-sm sm:min-w-[18rem]">
            <div className="mb-3 flex items-center justify-between gap-2">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 w-9 shrink-0 px-0"
                    aria-label={t('dayPagination.previousYear')}
                    onClick={() => setViewYear((y) => y - 1)}
                >
                    <span className="inline-flex size-5 shrink-0 items-center justify-center text-lg leading-none" aria-hidden={true}>
                        ←
                    </span>
                </Button>
                <span className="text-xs font-bold tabular-nums text-gray-900">{viewYear}</span>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 w-9 shrink-0 px-0"
                    aria-label={t('dayPagination.nextYear')}
                    onClick={() => setViewYear((y) => y + 1)}
                >
                    <span className="inline-flex size-5 shrink-0 items-center justify-center text-lg leading-none" aria-hidden={true}>
                        →
                    </span>
                </Button>
            </div>
            <div className="grid grid-cols-3 gap-2">
                {Array.from({ length: 12 }, (_, m) => {
                    const label = monthFormatter.format(new Date(viewYear, m, 1));
                    const isSelected = viewYear === selectedYear && m === selectedMonth;
                    return (
                        <button
                            key={m}
                            type="button"
                            onClick={() => onSelectMonth(new Date(viewYear, m, 1))}
                            className={`rounded-lg py-2.5 text-center text-sm font-medium transition-colors ${
                                isSelected
                                    ? 'bg-amber-100 text-gray-900 shadow-sm ring-1 ring-amber-400 ring-inset'
                                    : 'text-gray-700 hover:bg-amber-50'
                            }`}
                        >
                            {isJaLocale ? <JaMonthGridAbbrev label={label} /> : label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
